// code-guardian-ignore-duplicates: parallel master-page shell (tab kept in ?tab=, selectTab, the StoreMasterPage tab strip + action slot) copied on purpose; tab labels are functions of the balance wording
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Package, FolderTree } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import ProductsPage from './ProductsPage';
import ProductGroupsPage from './ProductGroupsPage';
import { useBalanceLabel } from '../hooks/useBalanceLabel';

/**
 * Master Data → Products.
 *
 * The per-project product catalogue that task questions read from, instead of
 * every task re-declaring the whole product list in its own cascading option
 * matrix (or worse, baking the SKU into the question text, which is why the
 * reporting engine still recovers per-SKU figures with an ILIKE).
 *
 * ONE menu entry with tabs, not one per level: folders and products are
 * cross-edited constantly — you file a product while looking at the tree — and
 * migration 076 records the lesson from Store Categories that two nav entries
 * "only meant leaving the page to cross-edit".
 *
 * Same embedded-tabs convention as StoreMasterPage: each tab is a standalone
 * page in `embedded` mode keeping its own fetching and modals, and this shell
 * supplies only the heading, the tab strip and a slot the active tab portals its
 * primary actions into.
 */

type Tab = 'products' | 'folders';

// Copy rule for this page: describe what the admin is looking at, never a
// worked example from some other project. The Products blurb used to cite "8
// sizes, 4 colours, 2 Bluetooth options" — which read as nonsense on a laptop
// catalogue and was the single most confusing thing on the screen.
// `blurb` is a function of the project's word for what it counts, because the
// third tab's whole subject is that word — see useBalanceLabel.
const TABS: { key: Tab; label: (noun: string) => string; icon: typeof Package; blurb: (lower: string) => string }[] = [
  {
    key: 'products',
    label: () => 'Products',
    icon: Package,
    blurb: () =>
      'The products this project sells, and every version of each one. A version is what you would actually count on a shelf — a specific size, colour or configuration. Tasks read this list, so you maintain it here once instead of retyping it into every task.',
  },
  {
    // "Hierarchy", not "Folders": the thing on screen is a nested tree of
    // brand → range → model, and calling it a folder undersold it as filing
    // rather than the shape of the catalogue.
    key: 'folders',
    label: () => 'Hierarchy',
    icon: FolderTree,
    blurb: () =>
      'Optional. The hierarchy groups products together — useful when a project carries several brands or ranges. If you have one level, or none, nothing is missing: products work perfectly well without it.',
  },
];

export default function ProductMasterPage() {
  // The tab lives in the URL so a link can point straight at either half, and so
  // the browser back button steps between them rather than leaving the page.
  const [searchParams, setSearchParams] = useSearchParams();
  const fromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState<Tab>(
    TABS.some((t) => t.key === fromUrl) ? (fromUrl as Tab) : 'products',
  );

  // The tab strip and the active tab's primary actions share one row. Each tab
  // portals its own actions in, so ownership stays with the page that handles
  // them. A callback ref (not useRef) because children must re-render once the
  // node exists.
  const [actionSlot, setActionSlot] = useState<HTMLDivElement | null>(null);

  const selectedProjectId = useAuthStore((state) => state.selectedProjectId);
  const balanceWords = useBalanceLabel(selectedProjectId);

  const selectTab = (tab: Tab) => {
    setActiveTab(tab);
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    setSearchParams(next, { replace: true });
  };

  const current = TABS.find((t) => t.key === activeTab) ?? TABS[0];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Products</h1>
        <p className="mt-1 text-sm text-gray-500">{current.blurb(balanceWords.lower)}</p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex w-fit flex-wrap items-center gap-1 rounded-lg border border-gray-100 bg-gray-50 p-1">
          {TABS.map((t) => {
            const Icon = t.icon;
            const isActive = t.key === activeTab;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => selectTab(t.key)}
                className={`flex items-center gap-1.5 rounded-md px-4 py-1.5 text-sm font-semibold transition-colors ${
                  isActive ? 'bg-white text-primary-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                <Icon className="h-4 w-4" />
                {t.label(balanceWords.noun)}
              </button>
            );
          })}
        </div>
        <div ref={setActionSlot} className="flex items-center gap-3" />
      </div>

      {/* Mounted on demand: each tab fetches on mount, so rendering the hidden
          one would load a catalogue the user may never open. */}
      {activeTab === 'products' && <ProductsPage embedded actionSlot={actionSlot} />}
      {activeTab === 'folders' && <ProductGroupsPage embedded actionSlot={actionSlot} />}
    </div>
  );
}
